/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { useEffect, useMemo, useRef } from "react";
import type { FunctionComponent, JSX } from "react";

import {
  NavigationEngine,
  createEasInputBlockInterceptor,
  createInputModeInterceptor,
  createLoggerInterceptor,
  createLongPressInterceptor,
  createRepeatGuardInterceptor,
  createScrollThrottleInterceptor,
  detectPlatform,
  registerTizenKeys
} from "../../model/navigation";
import { InputAction } from "../../model/enums";
import { DeviceModel } from "../../model/device";
import { useUnitySelector } from "../../viewModel";
import { selectActiveScreen } from "../../viewModel/screens";
import {
  NavigationContext,
  useAppVersionOverrideCode,
  useFeatureOverrideCode,
  useFeatureOverrideInput,
  useSecretCodeReset,
  useVideoDebugOverlayTrigger
} from "../hooks";

const WEBOS_SCROLL_THROTTLE_MS: number = 200;
const BROWSER_SCROLL_THROTTLE_MS: number = 100;
const LONG_PRESS_EXIT_DURATION_MS: number = 1500;

/**
 * Provides the NavigationEngine instance to the component tree via React context.
 *
 * - Detects the platform and configures the appropriate key mapper
 * - Registers built-in interceptors (input mode, logger, repeat guard, scroll throttle)
 * - Starts listening for input events on mount, stops on unmount
 * - Reads `activeScreen` from Redux to drive screen-level input routing
 *
 * ## Engine creation
 *
 * The engine is constructed synchronously via `useMemo` so that the context
 * value is never `null` on the initial render. Children calling
 * `useNavigationEngine()` will always receive a valid engine instance, even
 * before the first `useEffect` has fired.
 *
 * `activeScreen` is mirrored into a ref so the engine's `getActiveScreen`
 * getter always reads the latest Redux value at call time. The ref is
 * initialised with the current Redux value before the engine is constructed,
 * so any key event that arrives before the first sync effect is also routed
 * to the correct screen.
 *
 * Side effects (starting event listeners, Tizen key registration) are
 * confined to `useEffect` to keep the render phase pure.
 */
const NavigationProvider: FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }): JSX.Element => {
  const activeScreen = useUnitySelector(selectActiveScreen);

  // Mirror activeScreen into a ref so the engine getter always reads the
  // latest value without the engine needing to re-subscribe to Redux.
  const activeScreenRef = useRef(activeScreen);

  // Keep the ref in sync with every Redux update.
  useEffect((): void => {
    activeScreenRef.current = activeScreen;
  }, [activeScreen]);

  // Create the engine synchronously so the context is never null on first render.
  // useMemo is stable for the lifetime of this provider — no deps that change.
  const engine = useMemo((): NavigationEngine => {
    const platform = detectPlatform();
    const isPointerEnabled = platform !== "tizen" && platform !== "vegaos";

    const nav = new NavigationEngine({
      platform,
      enablePointer: isPointerEnabled,
      getActiveScreen: (): string => ""
    });

    return nav;
    // activeScreenRef is a stable ref object (same identity across renders);
    // the closure reads `.current` at call time, not at creation time, so the
    // engine always sees the latest active screen without needing a dep on the
    // ref. The engine itself must be stable (created once), hence the empty array.
  }, []);

  // Register the secret code reset interceptor (571247 → clear DB + splash).
  useSecretCodeReset(engine);

  // Register the debug-only web-app version override code (54321 → overlay).
  // No-op in release builds — see useAppVersionOverrideCode.
  useAppVersionOverrideCode(engine);

  // Register the debug-only Feature Override code (123456 → screen).
  // No-op in release builds — see useFeatureOverrideCode.
  useFeatureOverrideCode(engine);

  // Captures D-pad input for the Feature Override screen while it is open, so
  // it works from any context regardless of the active screen controller.
  useFeatureOverrideInput(engine);

  // Registers the Video Debug Overlay triggers (UP UP DOWN DOWN RIGHT on a
  // remote, Ctrl+Shift+D on desktop). Installed in every build by design —
  // see useVideoDebugOverlayTrigger; what release withholds is the
  // restricted metrics, not the overlay.
  useVideoDebugOverlayTrigger(engine);

  // Side effects: register interceptors, start event listeners, and register
  // platform keys. Cleanup destroys the engine when the provider unmounts.
  useEffect((): (() => void) => {
    const platform = detectPlatform();
    const isPointerEnabled = platform !== "tizen" && platform !== "vegaos";
    const isDev = process.env.NODE_ENV === "development";

    // Wire the activeScreenRef so the engine always reads the latest Redux
    // value at key-event time without re-creating the engine.
    engine.setActiveScreenGetter((): string => activeScreenRef.current);

    // `useFirst`, not `use`: while an EAS alert is on screen every input
    // except its dismiss key must be swallowed before any other interceptor
    // sees it. `use()` only appends, and several interceptors are already on
    // the chain by the time this effect runs — the secret-code reset and the
    // version override are registered by hooks called above it, and every
    // child screen's `useInactivityOsd` registers before its parent's effects
    // at all. Prepending is what actually puts this first; registering it
    // here first is not. See createEasInputBlockInterceptor.
    engine
      .getInterceptors()
      .useFirst("easInputBlock", createEasInputBlockInterceptor());

    engine.getInterceptors().use(
      "inputMode",
      createInputModeInterceptor((newMode): void => {
        document.body.classList.toggle("pointer-mode", newMode === "pointer");
        document.body.classList.toggle("keyboard-mode", newMode === "key");
        document.body.style.cursor = newMode === "pointer" ? "default" : "none";
      })
    );
    engine.getInterceptors().use("logger", createLoggerInterceptor(isDev));
    engine.getInterceptors().use("repeatGuard", createRepeatGuardInterceptor());

    if (isPointerEnabled) {
      const throttleMs =
        platform === "webos"
          ? WEBOS_SCROLL_THROTTLE_MS
          : BROWSER_SCROLL_THROTTLE_MS;

      engine
        .getInterceptors()
        .use("scrollThrottle", createScrollThrottleInterceptor(throttleMs));
    }

    if (platform === "tizen" || DeviceModel.isTizenPlatform()) {
      registerTizenKeys();

      const longPressHandle = createLongPressInterceptor([
        {
          action: InputAction.BACK,
          duration: LONG_PRESS_EXIT_DURATION_MS,
          onLongPress: (): void => {
            DeviceModel.closeApplication();
          }
        }
      ]);
      engine.getInterceptors().use("longPress", longPressHandle.interceptor);

      engine.getInterceptors().use("exitKey", (event): void => {
        if (
          event.action === InputAction.EXIT &&
          event.eventType === "keydown"
        ) {
          DeviceModel.closeApplication();
          event.stopped = true;
        }
      });

      engine.start();

      return (): void => {
        longPressHandle.dispose();
        engine.destroy();
      };
    }

    engine.start();

    return (): void => {
      engine.destroy();
    };
  }, [engine]); // engine is stable (useMemo with no deps), so this runs once

  return (
    <NavigationContext.Provider value={engine}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationProvider;
